// S43 — Indicateur discret : l'utilisateur a sa propre clé OpenRouter
// S44 — Popover hover : statut + crédits + actions Vérifier / Désactiver direct
// Apparait dans le header toolbar uniquement si has_openrouter_key = true
// Clic → onglet Profil (édition complète) · Hover → popover compact

import React, { useEffect, useState, useRef } from 'react';
import { KeyRound, RefreshCw, PowerOff, Power, ExternalLink, Check, AlertCircle, Lock } from 'lucide-react';
import { confirm as confirmModal, toast } from '../ui/Toast';

export default function UserKeyIndicator({ onNavigate }) {
  const [profile, setProfile] = useState(null);
  const [credits, setCredits] = useState(null);
  const [verifyError, setVerifyError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [hover, setHover] = useState(false);
  const hoverTimerRef = useRef(null);

  const headers = () => ({
    Authorization: `Bearer ${localStorage.getItem('oracle_token') || ''}`,
    'Content-Type': 'application/json',
  });

  const loadProfile = async () => {
    try {
      const res = await fetch('/api/auth/me', { headers: headers() });
      if (!res.ok) return;
      const data = await res.json();
      setProfile(data);
    } catch { /* silent */ }
  };

  useEffect(() => {
    loadProfile();
    const id = setInterval(loadProfile, 120000);
    return () => clearInterval(id);
  }, []);

  // Auto-vérifier les crédits dès qu'on hover (1 seul appel, cache 5 min)
  useEffect(() => {
    if (!hover || !profile?.api_key_active) return;
    if (credits || verifyError || verifying) return;
    verifyKey();
  }, [hover, profile?.api_key_active]);

  const verifyKey = async () => {
    setVerifying(true);
    setVerifyError(null);
    try {
      const res = await fetch('/api/auth/verify-openrouter-key', { method: 'POST', headers: headers() });
      const data = await res.json();
      if (data.valid && data.credits) {
        // Backend renvoie { total, usage, remaining } — on mappe sur notre structure UI
        setCredits({
          remaining: data.credits.remaining,
          used: data.credits.usage,
          limit: data.credits.total,
        });
      } else if (data.valid) {
        // Clé valide mais pas de crédits renvoyés (cas rare)
        setVerifyError('Clé valide, crédits indisponibles');
      } else {
        setVerifyError(data.error || 'Clé invalide');
      }
    } catch {
      setVerifyError('Erreur réseau');
    } finally {
      setVerifying(false);
    }
  };

  const toggleKey = async () => {
    const isActive = !!profile?.api_key_active;
    // Verrou admin : désactiver (= revenir au compte commun) est interdit. Réactiver reste permis.
    if (isActive && !!profile?.openrouter_key_locked) {
      toast.error("Compte OpenRouter verrouillé par l'administration : impossible de revenir au compte commun.");
      return;
    }
    const action = isActive ? 'désactiver' : 'réactiver';
    const confirmed = await confirmModal(
      `${isActive ? 'Désactiver' : 'Réactiver'} ta clé OpenRouter ? ${isActive ? 'Tes appels IA seront facturés sur la plateforme.' : 'Tes appels IA seront à nouveau facturés sur ton compte OpenRouter.'}`,
      { confirmText: isActive ? 'Désactiver' : 'Réactiver', danger: isActive }
    );
    if (!confirmed) return;
    setToggling(true);
    try {
      const url = isActive
        ? '/api/auth/openrouter-key/disable'
        : '/api/auth/openrouter-key/enable';
      const res = await fetch(url, { method: 'POST', headers: headers() });
      if (res.ok) {
        toast.success(isActive ? 'Clé désactivée' : 'Clé réactivée');
        setCredits(null);
        setVerifyError(null);
        await loadProfile();
      } else {
        toast.error(`Erreur ${action}`);
      }
    } catch { toast.error(`Erreur ${action}`); }
    setToggling(false);
  };

  // Délai d'ouverture/fermeture pour éviter les flickers
  const openSoon = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setHover(true), 120);
  };
  const closeSoon = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setHover(false), 200);
  };

  if (!profile?.has_openrouter_key) return null;

  const isActive = !!profile.api_key_active;
  const locked = !!profile.openrouter_key_locked;
  const statusColor = isActive ? 'rgb(16, 185, 129)' : 'rgb(245, 158, 11)';
  const usedPct = credits?.limit > 0 ? Math.round((credits.used / credits.limit) * 100) : null;

  return (
    <div
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={openSoon}
      onMouseLeave={closeSoon}
    >
      <button
        onClick={() => onNavigate && onNavigate('profile')}
        title={isActive ? 'Clé OpenRouter active — clic = aller au profil' : 'Clé OpenRouter désactivée — clic = aller au profil'}
        style={{
          background: 'transparent',
          border: '1px solid var(--card-border)',
          color: 'var(--text-secondary)',
          borderRadius: 999,
          padding: '4px 10px',
          fontSize: 11,
          fontWeight: 500,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          cursor: 'pointer',
          transition: 'all 0.15s',
        }}>
        <span style={{
          width: 6, height: 6, borderRadius: '50%',
          background: statusColor,
          boxShadow: `0 0 4px ${statusColor}`,
          flexShrink: 0,
        }} />
        <KeyRound size={12} />
        <span>Ma clé</span>
        {!isActive && <span style={{ fontSize: 9, opacity: 0.85 }}>· OFF</span>}
      </button>

      {hover && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            zIndex: 1000,
            minWidth: 280,
            background: 'var(--card-bg, #1a1f2e)',
            border: '1px solid var(--card-border, rgba(255,255,255,0.1))',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
            padding: '14px 16px',
            color: 'var(--text, #E8EDF5)',
            fontSize: 12,
            cursor: 'default',
          }}
          onMouseEnter={openSoon}
          onMouseLeave={closeSoon}
        >
          {/* Statut */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: statusColor,
              boxShadow: `0 0 6px ${statusColor}`,
            }} />
            <div style={{ fontWeight: 600, color: statusColor }}>
              {isActive ? 'Clé active' : 'Clé désactivée'}
            </div>
          </div>

          {/* Crédits */}
          {isActive && (
            <div style={{ marginBottom: 12 }}>
              {verifying ? (
                <div style={{ fontSize: 11, color: 'var(--text-secondary, #8A9BBE)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <RefreshCw size={11} style={{ animation: 'spin 0.8s linear infinite' }} />
                  Vérification…
                </div>
              ) : verifyError ? (
                <div style={{ fontSize: 11, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertCircle size={12} /> {verifyError}
                </div>
              ) : credits ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                    <span style={{ color: 'var(--text-secondary, #8A9BBE)' }}>Crédits restants</span>
                    <span style={{ fontWeight: 700, color: '#3b82f6' }}>${credits.remaining?.toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-secondary, #8A9BBE)', marginBottom: 6 }}>
                    <span>Utilisé : ${credits.used?.toFixed(2)}</span>
                    <span>/ ${credits.limit?.toFixed(2)}</span>
                  </div>
                  {usedPct !== null && (
                    <div style={{
                      height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2,
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        height: '100%', width: `${Math.min(usedPct, 100)}%`,
                        background: usedPct >= 90 ? '#ef4444' : usedPct >= 70 ? '#f59e0b' : '#3b82f6',
                        transition: 'width 0.3s',
                      }} />
                    </div>
                  )}
                </>
              ) : null}
            </div>
          )}

          {/* Actions rapides */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {isActive && (
              <button
                onClick={() => { setCredits(null); setVerifyError(null); verifyKey(); }}
                disabled={verifying}
                style={{
                  flex: 1,
                  padding: '6px 10px', borderRadius: 6,
                  background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.35)',
                  color: '#3b82f6', fontSize: 11, fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                  cursor: verifying ? 'wait' : 'pointer',
                  transition: 'all 0.15s',
                }}>
                <RefreshCw size={11} /> Vérifier
              </button>
            )}
            {locked && isActive ? (
              <div title="Verrouillé par l'administration : vous ne pouvez pas revenir au compte commun. Vous pouvez remplacer votre clé dans Mon profil."
                style={{
                  flex: 1, padding: '6px 10px', borderRadius: 6,
                  background: 'rgba(148,163,184,0.12)', border: '1px solid rgba(148,163,184,0.35)',
                  color: '#94a3b8', fontSize: 11, fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: 'not-allowed',
                }}>
                <Lock size={11} /> Verrouillé par l'admin
              </div>
            ) : (
              <button
                onClick={toggleKey}
                disabled={toggling}
                style={{
                  flex: 1,
                  padding: '6px 10px', borderRadius: 6,
                  background: isActive ? 'rgba(245,158,11,0.12)' : 'rgba(16,185,129,0.12)',
                  border: `1px solid ${isActive ? 'rgba(245,158,11,0.35)' : 'rgba(16,185,129,0.35)'}`,
                  color: isActive ? '#f59e0b' : '#10b981',
                  fontSize: 11, fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                  cursor: toggling ? 'wait' : 'pointer',
                  transition: 'all 0.15s',
                }}>
                {isActive ? <PowerOff size={11} /> : <Power size={11} />}
                {isActive ? 'Désactiver' : 'Réactiver'}
              </button>
            )}
          </div>

          {/* Lien profil */}
          <button
            onClick={() => onNavigate && onNavigate('profile')}
            style={{
              width: '100%', marginTop: 10, padding: '6px 10px', borderRadius: 6,
              background: 'transparent', border: '1px solid var(--card-border, rgba(255,255,255,0.1))',
              color: 'var(--text-secondary, #8A9BBE)', fontSize: 11, fontWeight: 500,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              cursor: 'pointer', transition: 'all 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--text, #E8EDF5)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary, #8A9BBE)'}>
            <ExternalLink size={11} /> Gérer dans Mon profil
          </button>
        </div>
      )}
    </div>
  );
}
